import { useState, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Mail, MessageSquare } from 'lucide-react'
import type { Contact, SelectedContact } from '@/routes/_public/app'

interface ContactSelectionProps {
  contacts: Contact[]
  onComplete: (selectedContacts: SelectedContact[]) => void
}

type FilterType = 'all' | 'sms' | 'email'

export function ContactSelection({
  contacts,
  onComplete,
}: ContactSelectionProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [channelPrefs, setChannelPrefs] = useState<
    Record<string, { sms: boolean; email: boolean }>
  >({})
  const [filter, setFilter] = useState<FilterType>('all')

  const filteredContacts = useMemo(() => {
    if (filter === 'sms') {
      return contacts.filter((c) => c.phone)
    } else if (filter === 'email') {
      return contacts.filter((c) => c.email)
    }
    return contacts
  }, [contacts, filter])

  const smsCount = contacts.filter((c) => c.phone).length
  const emailCount = contacts.filter((c) => c.email).length

  const toggleContact = (id: string) => {
    const newSelected = new Set(selectedIds)
    const contact = contacts.find((c) => c.id === id)
    if (newSelected.has(id)) {
      newSelected.delete(id)
      setChannelPrefs((prev) => {
        const next = { ...prev }
        delete next[id]
        return next
      })
    } else {
      newSelected.add(id)
      if (contact) {
        const hasPhone = Boolean(contact.phone)
        const hasEmail = Boolean(contact.email)
        setChannelPrefs((prev) => ({
          ...prev,
          [id]: {
            sms: hasPhone,
            email: hasPhone ? false : hasEmail,
          },
        }))
      }
    }
    setSelectedIds(newSelected)
  }

  const selectAll = () => {
    const allIds = new Set(filteredContacts.map((c) => c.id))
    setSelectedIds(allIds)
    setChannelPrefs((prev) => {
      const next = { ...prev }
      filteredContacts.forEach((contact) => {
        const hasPhone = Boolean(contact.phone)
        const hasEmail = Boolean(contact.email)
        next[contact.id] = {
          sms: hasPhone,
          email: hasPhone ? false : hasEmail,
        }
      })
      return next
    })
  }

  const deselectAll = () => {
    setSelectedIds(new Set())
    setChannelPrefs({})
  }

  const handleContinue = () => {
    if (selectedIds.size === 0) {
      alert('Please select at least one contact.')
      return
    }
    const selectedContacts = contacts
      .filter((contact) => selectedIds.has(contact.id))
      .map((contact) => {
        const prefs = channelPrefs[contact.id] || {
          sms: Boolean(contact.phone),
          email: Boolean(contact.email) && !contact.phone,
        }
        return {
          ...contact,
          sendSms: prefs.sms,
          sendEmail: prefs.email,
        }
      })
      .filter((contact) => contact.sendSms || contact.sendEmail)

    if (selectedContacts.length === 0) {
      alert('Please choose SMS or Email for at least one contact.')
      return
    }

    onComplete(selectedContacts)
  }

  const selectedSmsCount = Array.from(selectedIds).filter((id) => {
    const prefs = channelPrefs[id]
    return prefs?.sms
  }).length

  const selectedEmailCount = Array.from(selectedIds).filter((id) => {
    const prefs = channelPrefs[id]
    return prefs?.email
  }).length

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold">Select Recipients</h1>
        <p className="text-slate-600">
          Choose who should receive your message.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-lg">
              {contacts.length} Contact(s) Imported
            </CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={selectAll}
                disabled={filteredContacts.length === 0}
              >
                Select All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={deselectAll}
                disabled={selectedIds.size === 0}
              >
                Deselect All
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filter Tabs */}
          <Tabs
            value={filter}
            onValueChange={(v) => setFilter(v as FilterType)}
          >
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="all">All ({contacts.length})</TabsTrigger>
              <TabsTrigger value="sms">
                <MessageSquare className="h-4 w-4 mr-1" />
                SMS ({smsCount})
              </TabsTrigger>
              <TabsTrigger value="email">
                <Mail className="h-4 w-4 mr-1" />
                Email ({emailCount})
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Contact List */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredContacts.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No contacts match this filter.
              </div>
            ) : (
              <>
                <div className="hidden md:grid grid-cols-12 gap-3 px-3 text-xs uppercase tracking-wide text-slate-500">
                  <div />
                  <div className="col-span-3">Name</div>
                  <div className="col-span-3">Phone</div>
                  <div className="col-span-4">Email</div>
                  <div className="text-right">Use both</div>
                </div>
                {filteredContacts.map((contact) => {
                  const isSelected = selectedIds.has(contact.id)
                  const prefs = channelPrefs[contact.id] || {
                    sms: Boolean(contact.phone),
                    email: Boolean(contact.email) && !contact.phone,
                  }
                  const hasPhone = Boolean(contact.phone)
                  const hasEmail = Boolean(contact.email)
                  const useBoth = prefs.sms && prefs.email

                  const setSms = () => {
                    if (!isSelected || !hasPhone) return
                    setChannelPrefs((prev) => ({
                      ...prev,
                      [contact.id]: {
                        sms: true,
                        email: false,
                      },
                    }))
                  }

                  const setEmail = () => {
                    if (!isSelected || !hasEmail) return
                    setChannelPrefs((prev) => ({
                      ...prev,
                      [contact.id]: {
                        sms: false,
                        email: true,
                      },
                    }))
                  }

                  const toggleBoth = (checked: boolean) => {
                    if (!isSelected || !hasPhone || !hasEmail) return
                    setChannelPrefs((prev) => ({
                      ...prev,
                      [contact.id]: checked
                        ? { sms: true, email: true }
                        : { sms: true, email: false },
                    }))
                  }

                  return (
                    <div
                      key={contact.id}
                      className={`grid grid-cols-1 md:grid-cols-12 items-center gap-3 p-3 border rounded-lg ${
                        isSelected ? 'bg-white' : 'bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-3 md:contents">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleContact(contact.id)}
                          className="mt-1 md:mt-0"
                        />
                        <div className="min-w-0 md:col-span-3">
                          <Label className="font-semibold">{contact.name}</Label>
                        </div>
                      </div>
                      <div className="flex flex-col items-start gap-2 text-sm text-slate-600 md:col-span-3">
                        {contact.phone && (
                          <span className="flex items-center gap-1">
                            <MessageSquare className="h-3 w-3" />
                            {contact.phone}
                          </span>
                        )}
                        {hasPhone && hasEmail && (
                          <Checkbox
                            checked={prefs.sms}
                            onCheckedChange={(checked) => {
                              if (checked) setSms()
                            }}
                            disabled={!isSelected || useBoth}
                            className="rounded-full"
                          />
                        )}
                      </div>
                      <div className="flex flex-col items-start gap-2 text-sm text-slate-600 md:col-span-4">
                        {contact.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {contact.email}
                          </span>
                        )}
                        {hasPhone && hasEmail && (
                          <Checkbox
                            checked={prefs.email}
                            onCheckedChange={(checked) => {
                              if (checked) setEmail()
                            }}
                            disabled={!isSelected || useBoth}
                            className="rounded-full"
                          />
                        )}
                      </div>
                      <div className="flex justify-end">
                        {hasPhone && hasEmail && (
                          <Checkbox
                            checked={useBoth}
                            onCheckedChange={(checked) =>
                              toggleBoth(Boolean(checked))
                            }
                            disabled={!isSelected}
                          />
                        )}
                      </div>
                    </div>
                  )
                })}
              </>
            )}
          </div>

          {/* Summary */}
          <div className="bg-slate-50 p-4 rounded-lg space-y-2">
            <p className="font-semibold text-sm">Selected Summary:</p>
            <div className="flex gap-4 text-sm">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-slate-600" />
                <span>
                  <strong>{selectedSmsCount}</strong> via SMS
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-slate-600" />
                <span>
                  <strong>{selectedEmailCount}</strong> via Email
                </span>
              </div>
            </div>
            <p className="text-xs text-slate-600 mt-2">
              SMS is selected by default when a phone number is available.
            </p>
          </div>

          <Button
            onClick={handleContinue}
            className="w-full"
            size="lg"
            disabled={selectedIds.size === 0}
          >
            Continue with {selectedIds.size} Contact(s)
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
